import {  ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {  CimClass, IconGroups, NetworkStructure, SubstationTemplateDto, VisualizationConfigurationDto } from '../../models/network-structure.model';
import { Voltage } from '../../enums/voltage';  
import { ToastrService } from 'ngx-toastr';
import { SubstationTemplateService } from '../../services/substation-template.service';
import { UtilitySharedService } from '../../services/shared//utility-shared.service';
import { PermissionsService } from '../../services/permissions.service';

@Component({
  selector: 'lib-network-model-customization',
  templateUrl: './network-model-customization.component.html',
  styleUrls: ['./network-model-customization.component.scss'],
  standalone: false
})

export class NetworkModelCustomizationComponent implements OnInit {

  CIMClass = CimClass;
  Voltage = Voltage;
  networkStructures: NetworkStructure[] = [];
  substationTemplateToSave: SubstationTemplateDto = new SubstationTemplateDto();
  substationTemplates: SubstationTemplateDto[] = [];
  initialVoltageLevel: Voltage | null = null;

  selectedIconName: string = '';

  color: string = '#E89623';
  showPicker = false;
  iconGroups = IconGroups;

  utilityId!: number;

  symbolModalOpened: boolean = false;
  colorPickerOpened: boolean = false;
  isSchematicOpen: boolean = false;
  closingSchematic: boolean = false;

  ssName: string = '';

  @ViewChild('addSubstationModal') modalRef!: ElementRef;
  @ViewChild('openSubstationModalBtn') openSubstationModalBtn!: ElementRef<HTMLButtonElement>;

  constructor(private toastr: ToastrService,
              private router: Router,
              private route: ActivatedRoute, 
              private ssTemplateService: SubstationTemplateService,
              private cdr: ChangeDetectorRef,
              private utilitySharedService: UtilitySharedService,
              public permissionsService: PermissionsService,
            ) {}

  ngOnInit(): void {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
    this.getAll();
  }

  private getAll() {
    this.ssTemplateService.getAll(this.utilityId).subscribe({
      next: (response: any) => {
        this.substationTemplates = response?.body?.data;
      }
    })
    this.cdr.detectChanges();
  }

  onAddEquipment(ss: SubstationTemplateDto) {
    this.router.navigate(
      ['add-equipment-and-connectivity', ss.id],
      { relativeTo: this.route }
    );
  }

  selectIcon(icon: string, event: Event) {
    event.stopPropagation();
    this.selectedIconName = icon;
    this.substationTemplateToSave.visualizationConfiguration.symbol = icon;
    this.symbolModalOpened = false;
  }

  onColorChange(event: any) {
    this.color = event.color.hex;
    this.substationTemplateToSave.visualizationConfiguration.color = event.color.hex;
  }

  closeColorPicker(event: Event) {
    event.stopPropagation();
    this.colorPickerOpened = false;
  }

  get iconsForSubstation(): string[] {
    const voltage = this.substationTemplateToSave.voltageClass;
    if (voltage === Voltage.MV) {
      return this.iconGroups[CimClass.Substation]?.filter(icon => icon === 'Substation-2') || [];
    } else if (voltage === Voltage.LV) {
      return this.iconGroups[CimClass.Substation]?.filter(icon => icon === 'Substation-3') || [];
    } else if (voltage === Voltage.HV) {
      return this.iconGroups[CimClass.Substation]?.filter(icon => icon === 'Substation' || icon === 'Substation-1') || [];
    }
    return this.iconGroups[CimClass.Substation] || [];
  }

  onSave() {
    if (!this.substationTemplateToSave.visualizationConfiguration.color) {
      this.substationTemplateToSave.visualizationConfiguration.color = this.color;
    }
    if (this.substationTemplateToSave.className == '') {
      this.toastr.warning('All fields are mandatory.');
      return;     
    } else if (this.selectedIconName == '') {
      this.toastr.warning('All fields are mandatory.');
      return;
    } else if (this.substationTemplateToSave.voltageClass == null){
      this.toastr.warning('All fields are mandatory.');
      return;
    } 
    else {
      this.substationTemplateToSave.utilityId = this.utilityId;
      this.substationTemplateToSave.visualizationConfiguration.utilityId = this.utilityId;
      const isUpdate = this.substationTemplateToSave.id != null;
      const request$ = isUpdate
        ? this.ssTemplateService.updateSubstationTemplate(this.substationTemplateToSave)
        : this.ssTemplateService.addSubstationTemplate(this.substationTemplateToSave);
      request$.subscribe({
        next: (response: any) => {
          if (response?.body?.status == 200 || response?.body?.status == 'OK') {
            this.toastr.success(isUpdate ? 'Substation updated successfully.' : 'Substation added successfully.');
            this.getAll();
          } else {
            this.toastr.error(response?.body?.message)
          }
        }
      })
    }
  }

  onEditSubstation(ss: SubstationTemplateDto) {
    this.substationTemplateToSave = new SubstationTemplateDto({
      ...ss,
      visualizationConfiguration: new VisualizationConfigurationDto(ss.visualizationConfiguration),
    });
    this.substationTemplateToSave.voltageClass = ss.voltageClass;
    this.selectedIconName = ss.visualizationConfiguration?.symbol ?? '';
    this.color = ss.visualizationConfiguration?.color ?? this.color;
    this.symbolModalOpened = false;
    this.colorPickerOpened = false;
    this.initialVoltageLevel = null;
    this.cdr.detectChanges();
    this.initialVoltageLevel = ss.voltageClass ?? null;
    this.openSubstationModalBtn?.nativeElement.click();
  }

  openSchematicModal() {
    this.isSchematicOpen = true;
    this.closingSchematic = false;
  }

  closeSchematicModal() {
    this.closingSchematic = true;

    setTimeout(() => {
      this.isSchematicOpen = false;
    }, 500); 
  }

  closeSymbolModal(e: Event) {
    e.stopPropagation();
    this.symbolModalOpened = false;
  }

  onAddSubstation() {
    this.substationTemplateToSave = new SubstationTemplateDto();
    this.symbolModalOpened = false;
    this.colorPickerOpened = false;
    this.selectedIconName = '';
    this.initialVoltageLevel = null;
    this.cdr.detectChanges();
    this.initialVoltageLevel = Voltage.LV;
    this.substationTemplateToSave.voltageClass = Voltage.LV;
  }

  onSelectPrimaryVoltage(obj: Voltage) {
    this.substationTemplateToSave.voltageClass = obj;
  }
}